/**
 * IWINV AlimTalk Provider - Enhanced Implementation
 * Uses BaseAlimTalkProvider and Contract system for better structure
 */

import { BaseAlimTalkProvider } from '../abstract/provider.base';
import {
  ProviderCapabilities,
  ProviderConfiguration,
  ConfigurationField
} from '../contracts/provider.contract';

import { IWINVMessagingContract } from './contracts/messaging.contract';
import { IWINVTemplateContract } from './contracts/template.contract';
import { IWINVChannelContract } from './contracts/channel.contract';
import { IWINVAnalyticsContract } from './contracts/analytics.contract';
import { IWINVAccountContract } from './contracts/account.contract';

import type { IWINVConfig } from './types/iwinv';

export class IWINVProvider extends BaseAlimTalkProvider {
  public readonly id = 'iwinv';
  public readonly name = 'IWINV AlimTalk Provider';
  
  public readonly capabilities: ProviderCapabilities = {
    templates: {
      maxLength: 1000,
      maxVariables: 20,
      maxButtons: 5,
      supportedButtonTypes: ['WL', 'AL', 'DB', 'BK', 'MD'],
      requiresApproval: true,
      approvalTime: '1-2 days'
    },
    messaging: {
      maxRecipientsPerRequest: 1,
      maxRequestsPerSecond: 100,
      supportsBulk: false, // IWINV doesn't have native bulk API
      supportsScheduling: true,
      maxScheduleDays: 30,
      supportsFallback: true
    },
    channels: {
      requiresBusinessVerification: true,
      maxSenderNumbers: 10,
      supportsMultipleChannels: false
    }
  };

  // Contract implementations
  public templates: IWINVTemplateContract;
  public channels: IWINVChannelContract;
  public messaging: IWINVMessagingContract;
  public analytics: IWINVAnalyticsContract;
  public account: IWINVAccountContract;

  constructor(config?: IWINVConfig) {
    super(config);
    
    // Initialize contracts with config
    const iwinvConfig = this.getIWINVConfig();
    this.templates = new IWINVTemplateContract(iwinvConfig);
    this.channels = new IWINVChannelContract(iwinvConfig);
    this.messaging = new IWINVMessagingContract(iwinvConfig);
    this.analytics = new IWINVAnalyticsContract(iwinvConfig);
    this.account = new IWINVAccountContract(iwinvConfig);
  }

  public getConfigurationSchema(): ProviderConfiguration {
    return {
      required: [
        {
          key: 'apiKey',
          name: 'IWINV API Key',
          type: 'password',
          description: 'Your IWINV API key',
          required: true,
          validation: {
            pattern: '^[a-zA-Z0-9-_]+$',
            min: 10
          }
        }
      ],
      optional: [
        {
          key: 'baseUrl',
          name: 'Base URL',
          type: 'url',
          description: 'IWINV API base URL',
          required: false,
          default: 'https://alimtalk.bizservice.iwinv.kr',
          validation: {
            pattern: '^https?://.+'
          }
        },
        {
          key: 'debug',
          name: 'Debug Mode',
          type: 'boolean',
          description: 'Enable debug logging',
          required: false,
          default: false
        }
      ]
    };
  }

  protected async testConnectivity(): Promise<void> {
    const config = this.getIWINVConfig();
    
    try {
      const response = await fetch(`${config.baseUrl}/balance`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${config.apiKey}`
        }
      });

      if (!response.ok) {
        throw new Error(`Connectivity test failed: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      throw new Error(`Cannot connect to IWINV API: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  protected async testAuthentication(): Promise<void> {
    const config = this.getIWINVConfig();
    
    try {
      const response = await fetch(`${config.baseUrl}/balance`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${config.apiKey}`
        }
      });

      if (response.status === 401 || response.status === 403) {
        throw new Error('Invalid API key or insufficient permissions');
      }

      if (!response.ok) {
        const result = await response.json().catch(() => ({})) as Record<string, unknown>;
        throw new Error(`Authentication failed: ${(result.message as string) || response.statusText}`);
      }
    } catch (error) {
      if (error instanceof Error && error.message.includes('Authentication failed')) {
        throw error;
      }
      throw new Error(`Authentication test failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  protected getVersion(): string {
    return '2.0.0';
  }

  /**
   * Get IWINV-specific configuration
   */
  private getIWINVConfig(): IWINVConfig {
    return {
      apiKey: this.getConfig<string>('apiKey'),
      baseUrl: this.getConfig<string>('baseUrl') || 'https://alimtalk.bizservice.iwinv.kr',
      debug: this.getConfig<boolean>('debug') || false
    };
  }

  /**
   * IWINV-specific methods for backward compatibility
   */
  
  /**
   * Send AlimTalk message (legacy method)
   */
  async sendMessage(options: {
    templateCode: string;
    phoneNumber: string;
    variables: Record<string, any>;
    senderNumber?: string;
  }) {
    return this.messaging.send({
      templateCode: options.templateCode,
      phoneNumber: options.phoneNumber,
      variables: options.variables,
      senderNumber: options.senderNumber
    });
  }

  /**
   * Get account balance (legacy method)
   */
  async getBalance() {
    return this.account.getBalance();
  }

  /**
   * List templates (legacy method)
   */
  async listTemplates(filters?: any) {
    return this.templates.list(filters);
  }
}